/* global rwgps */
import React, {Fragment} from 'react'
import {findDOMNode} from 'react-dom'
import styles from './SidePanel.scss'
import {Distance, Elevation, Duration, Grade} from 'common/units'
import {photoGear} from 'lib/urlHelpers'
import {showGallery} from 'features/gallery/showGallery'
import {CommentsContainer} from 'common/Comments'
import {Actions} from '../Actions'
import {FindHills} from './FindHills'
import {Icon} from 'common/Icon'
import {CTA} from 'common/styleguide/CTA'
import {TriggerUpsellModal} from 'common/TriggerUpsellModal'
import moment from 'moment'
import {Topo} from 'common/styleguide/Topo'
import {CircleSpinner} from 'common/spinners'
import {FullBleedList} from './FullBleedList'
import {browserHistory} from 'react-router'
import {CueIcon} from 'common/CueIcon'
import _ from 'lodash'
import {flow, sortBy, uniqBy} from 'lodash/fp'
import cx from 'classnames'
import {formatText, pl} from 'lib/textUtils'
import {Map, MapSettings} from 'common/Map'
import {EDIT_ROUTE_DIALOG} from '../dialogs/EditRouteDialog'
import {userAgentIsMobile} from 'lib/mobileDeviceChecks'
import {OpenInAppButton} from 'common/OpenInAppButton'
import queryString from 'query-string'
import {orgVisibility, userVisibility} from 'lib/visibility'
import {SmartExportContainer} from './SmartExport'
import {userIsAdmin} from 'lib/utils'
import {Image} from 'common/Image'

const mobileMapSettings = MapSettings({
  showPOIs: false,
  showDistanceMarkers: false,
  showBikePaths: false
})

const OpenRouteInAppButton = ({route, ...props}) => {
  const query = queryString.stringify({
    privacy_code: route.privacyCode ? route.privacyCode : undefined
  })
  const appLink = query
    ? `rwgps://app/routes/${route.id}?${query}`
    : `rwgps://app/routes/${route.id}`

  return <OpenInAppButton appLink={appLink} {...props} />
}

// need to do something other than scale effect to make photos appear clickable
export class SidePanel extends React.PureComponent {
  constructor (props) {
    super(props)
    this.state = {
      scrollTop: 0,
      headerHeight: 9999,
      collapseHeight: 0
    }
  }

  componentDidMount () {
    window.addEventListener('resize', this.throttledOnResize)
    this.onResize()
  }

  componentWillUnmount () {
    window.removeEventListener('resize', this.throttledOnResize)
  }

  onResize = () => {
    this.setState({
      collapseHeight: findDOMNode(this._stickyHeader).scrollHeight
    })
  }

  throttledOnResize = _.throttle(this.onResize, Math.floor(1000 / 60))

  onContentScroll = (e) => {
    this.throttledOnContentScroll(e.target.scrollTop)
  }

  throttledOnContentScroll = _.throttle(
    (scrollTop) => this.setState({
      scrollTop,
      headerHeight: findDOMNode(this._header).scrollHeight - scrollTop
    }),
    Math.floor(1000 / 60), // aim for a 60fps header animation
    {leading: true}
  )

  render () {
    const {
      r,
      onCoursePointHovered,
      onCoursePointClicked,
      onSegmentSelected,
      onSegmentHovered,
      showDialog,
      currentUser,
      pinRoute,
      unpinRoute,
      segmentMatches,
      onTriggerFileBrowser,
      pendingPhotoUploads,
      onPhotoDeleted,
      mobile,
      fullscreen,
      editable,
      printRoute,
      onPointHighlighted
    } = this.props

    const {headerHeight, collapseHeight} = this.state
    const showCollapsedHeader = headerHeight <= collapseHeight
    const visibility = r.visibility > 0 ? (r.club ? orgVisibility : userVisibility)[r.visibility] : ''

    return (
      <div className={cx(styles.SidePanel, mobile && styles.mobile, fullscreen && styles.fullscreen)} onScroll={this.onContentScroll}>
        <Topo ref={r => { this._stickyHeader = r }} dark className={cx(styles.stickyHeader, showCollapsedHeader && styles.stickyHeaderVisible)}>
          <h1>{r.name}</h1>
          <Actions mini
            editable={editable}
            r={r}
            showDialog={showDialog}
            currentUser={currentUser}
            printRoute={printRoute}
            pinRoute={pinRoute}
            unpinRoute={unpinRoute} />
        </Topo>
        <div className={styles.scrollContent} onScroll={this.onContentScroll}>
          <Topo ref={r => { this._header = r }} dark>
            <Actions
              editable={editable}
              r={r}
              showDialog={showDialog}
              currentUser={currentUser}
              printRoute={printRoute}
              pinRoute={pinRoute}
              unpinRoute={unpinRoute} />
            <Masthead
              editable={editable}
              editDetails={() => showDialog(EDIT_ROUTE_DIALOG)}
              route={r}
              title={r.name}
              author={r.user}
              visibility={visibility} />
          </Topo>
          {mobile && <div className={styles.miniMap}>
            {r.trackPoints && <Map
              experimental
              showToolbar={false}
              showOverlayPicker={false}
              height='100%'
              overlay='rwgps'
              routes={[r]}
              defaultMapSettings={mobileMapSettings} />}
          </div>}
          <Metrics r={r} currentUser={currentUser} onPointHighlighted={onPointHighlighted} />
          <div className={styles.Main}>
            {(userAgentIsMobile()
              ? <OpenRouteInAppButton margins={false} route={r} />
              : <SmartExportContainer
                r={r}
                showDialog={showDialog}
                sendToDevice={() => browserHistory.push(`/routes/${r.id}/send_to_device`)} />)}
            <hr />
            {r.description && _.trim(r.description) !== '' && (
              <div>
                {formatText(r.description)}
                {editable && <a className={styles.editLink} onClick={() => showDialog(EDIT_ROUTE_DIALOG)}>Edit</a>}
                <hr />
              </div>
            )}
            <RouteMeta r={r} />
            <Photos
              onPhotoDeleted={onPhotoDeleted}
              r={r}
              editable={editable}
              pendingPhotoUploads={pendingPhotoUploads}
              onTriggerFileBrowser={onTriggerFileBrowser} />
            {r.coursePoints && r.coursePoints.length > 0 && <div>
              <h2>Cuesheet</h2>
              <Cuesheet
                coursePoints={r.coursePoints}
                onCoursePointHovered={onCoursePointHovered}
                onCoursePointClicked={onCoursePointClicked} />
            </div>}
            {r.coursePoints && r.coursePoints.length === 0 && <div className={styles.Cuesheet}>
              <h2>Cuesheet</h2>
              {editable
                ? <p>This route does not have a cuesheet. Visit our <a href='https://ridewithgps.com/help/trace-tool' target='_blank'>help page</a> to learn how to add a cuesheet to this route.</p>
                : <p>This route does not have a cuesheet. Look for the <Icon className={styles.light} i='md-document' data-tip title='Cuesheet' /> icon in listings to find routes with cuesheets.</p>}
            </div>}
            {segmentMatches && segmentMatches.length > 0
              ? <Segments
                segments={segmentMatches}
                onSegmentSelected={onSegmentSelected}
                onSegmentHovered={onSegmentHovered} />
              : <div className={styles.Segments}>
                <h2>Segments</h2>
                <p>There are no segments for this route. Visit our <a href='https://ridewithgps.com/help/segments' target='_blank'>help page</a> to learn how to create segments for a route.</p>
              </div>
            }
            <FindHills
              rawPoints={r && r.get('rawPoints')}
              onSegmentSelected={onSegmentSelected}
              onSegmentHovered={onSegmentHovered} />
            <CommentSection r={r} currentUser={currentUser} />
            {!currentUser.id && <div className={styles.SignupCTA}>
              <Topo mint className={styles.inner}>
                <h2>Like what you see?</h2>
                <p>Sign up for a free Ride with GPS account to save this route and send to your device.</p>
                <CTA secondary href='/signup'>Sign Up</CTA>
              </Topo>
            </div>}
          </div>
        </div>
      </div>
    )
  }
}

const Masthead = ({title, route, author, event, visibility, editable, editDetails}) => (
  <div className={styles.Masthead}>
    {editable
      ? <h1 id='main-route-title' className={styles.editTitle} onClick={editDetails}>{title} {editable && <Icon className={styles.icon} i='md-create' size={18} />}</h1>
      : <h1 id='main-route-title'>{title} {route.locked && <Icon classname={styles.icon} i='md-lock' size={20} data-tip title='This route has been locked by an administrator and cannot be modified' />}</h1>}
    <div className={styles.byline}>
      <span>
        By
        {' '}
        {route.bylineName
          ? <a href={route.bylinePath}>{route.bylineName}</a>
          : <a href={`/users/${author.id}`}>{author.name}</a>}
      </span>
      {/* TODO update the viz badge styles */}
      {visibility && <span className={cx(styles.privacyBadge, editable && styles.pointer)} onClick={() => editable && editDetails()}>{visibility}</span>}
    </div>
  </div>
)

export const Metrics = ({r, troute, objectType, onPointHighlighted}) => {
  // support both legacy RSP and TRSP
  const tr = troute || r

  const canSeeEstimatedTime = tr._permissions.navigate || _.get(tr.permissions, 'navigate')

  return (
    <div className={styles.Metrics}>
      <Distance value={tr.distance}>
        {m => <Metric metric={m} label='Distance' i='distance' />}
      </Distance>
      <Elevation value={tr.elevationGain}>
        {m => <Metric metric={m} label='Elevation Gain' i='md-add' />}
      </Elevation>
      <Grade value={tr.metrics.grade.max}>
        {m => <Metric
          metric={m}
          label='Max Grade'
          i='grade-up'
          onMouseEnter={() => onPointHighlighted({index: tr.metrics.grade.max_i}, `Max Grade: ${m.valueToString()}%`)}
          onMouseLeave={() => onPointHighlighted()} />}
      </Grade>
      {objectType === 'trip' && <Duration bigUnits value={tr.movingTime}>
        {m => <Metric metric={m} label='Moving Time' unitLabel='hrs' i='md-stopwatch' />}
      </Duration>}
      {objectType !== 'trip' && (canSeeEstimatedTime ? <EstimatedTime r={tr} /> : <UpsellEstimatedTime />)}
      <Elevation value={tr.elevationLoss}>
        {m => <Metric metric={m} label='Elevation Loss' i='md-remove' />}
      </Elevation>
      <Grade value={tr.metrics.grade.min}>
        {m => <Metric
          metric={m}
          label='Min Grade'
          i='grade-down'
          onMouseEnter={() => onPointHighlighted({index: tr.metrics.grade.min_i}, `Min Grade: ${m.valueToString()}%`)}
          onMouseLeave={() => onPointHighlighted()} />}
      </Grade>
    </div>
  )
}

class UpsellEstimatedTime extends React.PureComponent {
  render () {
    return (
      <TriggerUpsellModal className={styles.Metric} feature={'estimatedTime'}>
        <Icon className={styles.icon} i={'md-time'} title={'estimate time'} />
        <span className={styles.metricValue}>
          <Duration value={0}>
            {m => <Fragment>
              {m.valueToString()}
            </Fragment>}
          </Duration>
        </span>
        <span className={styles.premiumRequired}>
          <Icon i={'premium-arrow'} />
        </span>
      </TriggerUpsellModal>
    )
  }
}

class EstimatedTime extends React.PureComponent {
  constructor (props) {
    super(props)
    this.state = {
      mcResult: null
    }
  }

  componentDidMount () {
    this.computeMetrics()
  }

  componentDidUpdate () {
    if (!this.state.mcResult) {
      this.computeMetrics()
    }
  }

  computeMetrics = () => {
    const {r} = this.props
    if (!r.get('rawPoints')) { return }
    // keeping this around so that we get lots of warning if something causes
    // this (comparatively expensive) re-render
    console.log(`⚠️ calculated estimated time`)
    const tpsResult = rwgps.TrackPoint.fromJSON(r.get('rawPoints'))
    const points = tpsResult.trackPoints
    const data = new rwgps.Data(points)

    // in our case, setting readonly to false can't cause problems here because
    // the source data is immutable
    const mc = new rwgps.MetricsCalculator(data, false)
    const mcResult = mc.calculate()
    console.log('state', mcResult)
    this.setState({mcResult})
  }

  render () {
    const {mcResult} = this.state
    // the presence of a mcResult, but a missing/NaN estimatedTime implies that
    // we don't have enough data to calculate estimated time.
    if (!mcResult || !mcResult.estimatedTime) {
      return <Duration value={0} bigUnits>
        {m => <Metric
          noUnits
          className={styles.pendingMetric}
          metric={m}
          label='estimated time'
          i='md-time'
          tip={mcResult ? `We don't have enough ride data to calculate your estimated time` : 'Calculating Estimated Time...'} />}
      </Duration>
    }
    return (
      <Duration value={mcResult.estimatedTime} bigUnits>
        {m => <Metric noUnits metric={m} label={`estimated time (${m.short})`} i='md-time' />}
      </Duration>
    )
  }
}

const Metric = ({metric, noUnits, label, unitLabel, i, className, tip, ...props}) => (
  <div className={cx(styles.Metric, className)} {...props} data-tip title={tip || label}>
    <Icon className={styles.icon} i={i} title={label} />
    <span className={styles.metricValue}>
      {metric.valueToString()}{!noUnits && <span className={styles.units}>{unitLabel || metric.short}</span>}
    </span>
  </div>
)

const PRIVACY_LABELS = {
  '0': 'Public',
  '1': 'Private',
  '2': 'Friends Only'
}

const PRIVACY_ICONS = {
  '0': 'md-globe',
  '1': 'md-eye-off',
  '2': 'md-contact'
}

const RouteMetaItem = ({children, title, i}) => (
  <div className={styles.RouteMetaItem}>
    <div className={styles.container} data-tip title={title}>
      <div className={styles.icon}><Icon i={i} size={14} /></div>
      <div className={styles.text}>{children}</div>
    </div>
  </div>
)

const RouteMeta = ({r}) => (
  <div className={styles.RouteMeta}>
    <RouteMetaItem title='Start Location' i='md-pin'>{r.locality}, {r.administrativeArea}</RouteMetaItem>
    <RouteMetaItem title='Visibility' i={PRIVACY_ICONS['' + r.visibility]}>{PRIVACY_LABELS['' + r.visibility]} ({pl(r.views, 'view')})</RouteMetaItem>
    <RouteMetaItem title='Date Created' i='md-create'>Created {moment(r.createdAt).format('MMM D, YYYY')}</RouteMetaItem>
    <RouteMetaItem title='Last Updated' i='md-sync'>Updated {moment(r.updatedAt).format('MMM D, YYYY')}</RouteMetaItem>
    {r.copiedFromName && <RouteMetaItem i='md-link'>Copy of <a data-tip title={r.copiedFromName} href={r.copiedFromPath}>{r.copiedFromName}</a></RouteMetaItem>}
  </div>
)

const Photos = ({r, editable, onTriggerFileBrowser, pendingPhotoUploads, onPhotoDeleted}) => {
  const orderedPhotos = _.orderBy(r.photos, 'id', 'desc')
  const showGalleryWithProps = (activePhotoId) => showGallery({
    photos: orderedPhotos,
    readonly: !editable,
    parentType: 'route',
    parentId: r.id,
    activePhotoId,
    onPhotoDeleted
  })

  return <div className={styles.Photos}>
    {editable && r.photos.length === 0 && !pendingPhotoUploads && <Fragment>
      <h2>Photos</h2>
      <p>Drag photos onto this page or <a onClick={onTriggerFileBrowser}>click here</a> to add photos to this route.</p>
    </Fragment>}
    {pendingPhotoUploads > 0 && <p><CircleSpinner size={16} /> Uploading {pendingPhotoUploads} photo{pendingPhotoUploads > 1 ? 's' : ''}...</p>}
    {orderedPhotos.length === 1 && <Image id={orderedPhotos[0].id} width={360} height={240} onClick={() => showGalleryWithProps(orderedPhotos[0].id)} />}
    {orderedPhotos.length > 1 && <div className={styles.photoGrid}>
      {orderedPhotos.slice(0, 6).map(p => (
        <div className={styles.photo} key={p.id}>
          <img src={photoGear(p.id)} onClick={() => showGalleryWithProps(p.id)} />
        </div>
      ))}
    </div>}
    {r.photos.length > 0 && <div className={styles.morePhotos}>
      <span><a onClick={showGalleryWithProps}>Open gallery</a> ({r.photos.length} photo{r.photos.length > 1 ? 's' : ''})</span>
      {editable && <a onClick={onTriggerFileBrowser} data-tip title='You can also add photos by dragging them onto this page'>Add photos</a>}
    </div>}
  </div>
}

class Cuesheet extends React.PureComponent {
  constructor (props) {
    super(props)
    this.state = {
      expanded: false
    }
  }

  toggleExpand = () => {
    this.setState({expanded: !this.state.expanded}, () => {
      if (window.ga) {
        if (this.state.expanded) {
          window.ga('set', 'page', '/virtual/rspv2' + window.location.pathname + '/cuesheet')
        } else {
          window.ga('set', 'page', '/virtual/rspv2' + window.location.pathname)
        }
        window.ga('send', 'pageview')
      }
    })
  }

  render () {
    const {coursePoints, onCoursePointHovered, onCoursePointClicked} = this.props
    const {expanded} = this.state
    const points = expanded ? coursePoints : coursePoints.slice(0, 5)

    return (
      <div className={styles.Cuesheet}>
        <FullBleedList expanded={expanded}>
          {points.map((cp, index) => (
            <li key={cp.i}
              onClick={() => onCoursePointClicked(index)}
              onMouseOver={() => onCoursePointHovered(index)}
              onMouseOut={() => onCoursePointHovered(null)}>
              <div className={styles.cueIcon}><CueIcon type={cp.t} className={styles.cueImg} /></div>
              <div className={styles.cueText}>{cp.n}</div>
              <div style={{flex: '0 0 auto'}}><Distance value={cp.d} bigUnits maximumFractionDigits={1} minimumFractionDigits={1} short /></div>
            </li>
          ))}
        </FullBleedList>
        {coursePoints.length > 5 && <div className={styles.expandlink}>
          <a onClick={this.toggleExpand}>{expanded ? 'Collapse Cuesheet' : 'Show Full Cuesheet'}</a>
        </div>}
      </div>
    )
  }
}

class Segments extends React.PureComponent {
  constructor (props) {
    super(props)
    this.state = {
      expanded: false
    }
  }

  toggleExpand = () => {
    this.setState({expanded: !this.state.expanded})
  }

  render () {
    const {segments, onSegmentSelected, onSegmentHovered} = this.props
    const {expanded} = this.state
    const segs = flow(
      sortBy(s => -s.distance),
      uniqBy(s => s.segment_id),
      arr => expanded ? arr : arr.slice(0, 3)
    )(segments)

    return (
      <div className={styles.Segments}>
        <h2>Segments</h2>
        <FullBleedList>
          {segs.map((s) => (
            <li key={s.segment_id}
              className={styles.segment}
              onMouseOver={() => onSegmentHovered([s.start_index, s.end_index])}
              onMouseOut={() => onSegmentHovered(null)}
              onClick={() => onSegmentSelected([s.start_index, s.end_index], true)}>
              <div>{s.segment_title}</div>
              <div style={{flex: '0 0 auto'}}>
                <span className={styles.leaderboard}>
                  <a href={`/segments/${s.segment_id}`} target='_blank'><Icon i='md-pulse' data-tip title='View Leaderboard' /></a>
                </span>
                <span style={{display: 'inline-block', marginRight: '15px'}}>
                  +<Elevation value={s.ele_gain} short />
                </span>
                <Distance value={s.distance} maximumFractionDigits={1} minimumFractionDigits={1} short />
              </div>
            </li>
          ))}
        </FullBleedList>
        {segments.length > 3 && <div className={styles.expandlink}>
          <a onClick={this.toggleExpand}>{expanded ? 'Collapse Segments' : 'Show All Segments'}</a>
        </div>}
      </div>
    )
  }
}

class CommentSection extends React.PureComponent {
  render () {
    const {r, currentUser} = this.props

    const canModerate = r.get('user').id === currentUser.id || userIsAdmin

    return (
      <div className={styles.Comments}>
        <h2>Comments</h2>
        <CommentsContainer
          objectType='route'
          objectId={r.get('id')}
          privacyCode={r.get('privacyCode')}
          includeHeading={false}
          canModerate={canModerate} />
      </div>
    )
  }
}
